'use client';

import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Star, Pencil, CheckCircle, Check, Loader2, Trash2 } from 'lucide-react';
import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';
import { useUserReviews, useDeleteReview } from '@/hooks/useReviews';
import { useCurrentOrders } from '@/hooks/useOrders';
import { ReviewForm } from './ReviewForm';
import { StarRating } from './StarRating';
import Image from 'next/image';
import { Review } from '@/lib/Apis/models';
import { ProductsApi } from '@/lib/Apis';
import { getApiConfiguration } from '@/lib/api-adapter';

const getId = (obj: any): string => obj?.id || obj?._id || '';
const getVariantId = (item: any): string => item?.productVariantId || item?._id || item?.id || '';
const reviewKey = (orderId: string, variantId: string) => `${orderId}:${variantId}`;
const isProductAvailable = (item: any): boolean => {
  const variantData = item?.productVariantData || item?.variant;
  if (!variantData) return false;
  if (variantData.isActive === false) return false;
  if ((variantData as any).isDeleted === true) return false;
  if ((variantData as any).deletedAt) return false;
  if ((variantData as any).product?.isActive === false) return false;
  if ((variantData as any).product?.isDeleted === true) return false;
  if ((variantData as any).product?.deletedAt) return false;
  return true;
};

interface UnifiedItem {
  productId: string;
  variantId: string;
  orderId: string;
  name: string;
  variantName?: string;
  image?: string;
  review?: Review;
  orderDate?: string;
  category?: string;
}

export function AccountReviewsTab() {
  const { reviews, isLoading: reviewsLoading, refetch: refetchReviews } = useUserReviews();
  const { orders, isLoading: ordersLoading } = useCurrentOrders();
  const { deleteReview } = useDeleteReview();

  const [filter, setFilter] = useState<'all' | 'reviewed' | 'awaiting'>('all');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [expandedMode, setExpandedMode] = useState<'write' | 'edit' | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Collect all unique productIds from completed orders to verify they still exist
  const productIds = useMemo(() => {
    const completedStatuses = ['Delivered', 'Picked Up', 'Fulfilled'];
    const ids = new Set<string>();
    orders
      .filter((order) => completedStatuses.includes(order.orderStatus || ''))
      .forEach((order) => {
        order.items?.forEach((item: any) => {
          const variantData = item?.productVariantData || item?.variant;
          const productId = item.productId || variantData?.productId;
          if (productId) ids.add(productId);
        });
      });
    return Array.from(ids);
  }, [orders]);

  // Fetch each ordered product once: drives existence filtering AND supplies the
  // product image/name fallbacks (mirrors OrderCard's useOrderProductData behaviour).
  const { data: productData, isLoading: productsCheckLoading } = useQuery({
    queryKey: ['products', 'existence', productIds],
    queryFn: async () => {
      const api = new ProductsApi(getApiConfiguration());
      const results = await Promise.allSettled(productIds.map((id) => api.getSingleProduct(id)));
      const existing = new Set<string>();
      const images: Record<string, string> = {};
      const names: Record<string, string> = {};
      results.forEach((r, i) => {
        if (r.status !== 'fulfilled') return;
        const id = productIds[i];
        existing.add(id);
        const product = r.value?.data as any;
        const img = product?.media?.[0]?.file;
        if (img) images[id] = img;
        if (product?.name) names[id] = product.name;
      });
      return { existing, images, names };
    },
    enabled: productIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const existingProductIds = productData?.existing;
  const productImages = productData?.images;
  const productNames = productData?.names;

  const isLoading = reviewsLoading || ordersLoading || productsCheckLoading;

  const reviewMap = useMemo(() => {
    const map = new Map<string, Review>();
    reviews.forEach((r) => {
      if (r.orderId && r.productVariantId) {
        map.set(reviewKey(r.orderId, r.productVariantId), r);
      }
    });
    return map;
  }, [reviews]);

  const allItems = useMemo<UnifiedItem[]>(() => {
    const completedStatuses = ['Delivered', 'Picked Up', 'Fulfilled'];
    const items: UnifiedItem[] = [];
    const seen = new Set<string>();

    orders
      .filter((order) => completedStatuses.includes(order.orderStatus ? order.orderStatus : ''))
      .forEach((order) => {
        const orderId = getId(order);
        const allOrderItems = order.items ?? [];
        const availableItems = allOrderItems.filter((item: any) => {
          if (!isProductAvailable(item)) return false;
          const variantData = item?.productVariantData || item?.variant;
          const productId = item.productId || variantData?.productId;
          if (productId && existingProductIds && !existingProductIds.has(productId)) return false;
          return true;
        });

        availableItems.forEach((item: any) => {
          const variantId = getVariantId(item);
          const key = reviewKey(orderId, variantId);
          if (seen.has(key)) return;
          seen.add(key);

          const variantData = item.productVariantData || item.variant;
          // productId used to look up the fetched product image/name maps
          const lookupProductId = item.productId || variantData?.productId;
          const productId = lookupProductId || variantId;

          // Image: variant media first, else the fetched product image (mirrors OrderCard)
          const variantImg = variantData?.media?.[0]?.file;
          const isImgPlaceholder = !variantImg || variantImg === '/placeholder.png';
          const image = isImgPlaceholder
            ? ((lookupProductId ? productImages?.[lookupProductId] : undefined) || PLACEHOLDER_IMAGE_SRC)
            : variantImg;

          // Name: title = parent product name, subtitle = variant name (mirrors OrderCard)
          const variantName = variantData?.name;
          const productName =
            variantData?.productName ||
            (lookupProductId ? productNames?.[lookupProductId] : undefined) ||
            variantName ||
            'Product';
          const category = variantData?.category || variantData?.categoryName || variantData?.type;

          items.push({
            productId, variantId, orderId, name: productName, variantName, image,
            review: reviewMap.get(key),
            orderDate: order.createdAt ? String(order.createdAt) : undefined,
            category,
          });
        });
      });

    return items;
  }, [orders, reviewMap, existingProductIds, productImages, productNames]);

  const reviewedCount = useMemo(() => allItems.filter((i) => i.review).length, [allItems]);
  const filteredItems =
    filter === 'reviewed' ? allItems.filter((i) => i.review) :
    filter === 'awaiting' ? allItems.filter((i) => !i.review) :
    allItems;

  const getItemKey = (item: UnifiedItem) => `${item.orderId}:${item.variantId}`;

  const handleExpand = useCallback((item: UnifiedItem, mode: 'write' | 'edit') => {
    const key = getItemKey(item);
    if (expandedKey === key && expandedMode === mode) {
      setExpandedKey(null);
      setExpandedMode(null);
    } else {
      setExpandedKey(key);
      setExpandedMode(mode);
    }
  }, [expandedKey, expandedMode]);

  const handleFormSuccess = useCallback(() => {
    setExpandedKey(null);
    setExpandedMode(null);
    refetchReviews();
  }, [refetchReviews]);

  const collapseForm = useCallback(() => {
    setExpandedKey(null);
    setExpandedMode(null);
  }, []);

  const handleDelete = useCallback(async (reviewId: string) => {
    setDeletingId(reviewId);
    try {
      await deleteReview(reviewId);
      refetchReviews();
    } finally {
      setDeletingId(null);
    }
  }, [deleteReview, refetchReviews]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-100 rounded w-2/3" />
          <div className="flex gap-3 mt-4">
            <div className="h-16 w-32 bg-gray-200 rounded-xl" />
            <div className="h-16 w-36 bg-gray-100 rounded-xl" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 overflow-y-auto">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-hsecondary">My Reviews</h2>
        <p className="text-sm text-hmuted mt-1">
          Review the products you've purchased. Your honest feedback helps other customers.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="flex gap-3">
        <button
          onClick={() => setFilter(filter === 'reviewed' ? 'all' : 'reviewed')}
          className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-colors ${
            filter === 'reviewed'
              ? 'border-green-300 bg-green-50 border'
              : 'border-gray-200 border bg-white hover:border-green-200 hover:bg-green-50/50'
          }`}
        >
          <div className="size-10 rounded-full bg-[#10B9811A] flex items-center justify-center shrink-0">
            <CheckCircle className="size-5 text-[#10B981]" />
          </div>
          <div className="text-left">
            <p className="text-lg sm:text-2xl font-medium text-hsecondary leading-none">{reviewedCount}</p>
            <p className="text-xs text-hsecondary/40 mt-0.5">Reviewed</p>
          </div>
        </button>

        <button
          onClick={() => setFilter(filter === 'awaiting' ? 'all' : 'awaiting')}
          className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-colors ${
            filter === 'awaiting'
              ? 'border-amber-300 border bg-amber-50'
              : 'border-gray-200 border bg-white hover:border-amber-200 hover:bg-amber-50/50'
          }`}
        >
          <div className="size-10 rounded-full bg-[#FEF3C6] flex items-center justify-center shrink-0">
            <Star className="size-5 text-[#E17100]" />
          </div>
          <div className="text-left">
            <p className="text-lg sm:text-2xl font-medium text-hsecondary leading-none">{allItems.length - reviewedCount}</p>
            <p className="text-xs text-hsecondary/40 mt-0.5">Awaiting Review</p>
          </div>
        </button>
      </div>

      {/* Items List */}
      {filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 py-12 px-6 text-center">
          <div className="size-14 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="size-7 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-800">
              {filter === 'awaiting' ? 'No pending reviews' : filter === 'reviewed' ? 'No reviews yet' : 'All caught up!'}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">
              {filter === 'reviewed'
                ? 'Start sharing your experience with your purchased products.'
                : "You've reviewed all your completed orders."}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => {
            const { review } = item;
            const itemKey = getItemKey(item);
            const reviewId = review ? (review as any).id || (review as any)._id : null;
            const isDeleting = deletingId === reviewId;
            const isEditMode = expandedKey === itemKey && expandedMode === 'edit';
            const isWriteMode = expandedKey === itemKey && expandedMode === 'write';

            // Shared product header
            const productHeader = (
              <div className="flex items-start gap-3 p-4">
                {/* Image */}
                <div className="relative w-12 h-12 sm:w-14 sm:h-14 bg-gray-100 rounded-lg shrink-0 overflow-hidden">
                  <Image src={item.image || PLACEHOLDER_IMAGE_SRC} alt={item.name} fill className="object-cover" sizes="56px" />
                </div>
                {/* Info + Actions */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-semibold text-hsecondary truncate">{item.name}</p>
                      {item.variantName && item.variantName !== item.name && (
                        <p className="text-xs text-hsecondary/60 mt-0.5 truncate">{item.variantName}</p>
                      )}
                      {item.category && (
                        <p className="text-xs text-hsecondary/40 mt-0.5">{item.category}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-xs text-hsecondary/40">
                          Order #{item.orderId.slice(-8).toUpperCase()}
                          {item.orderDate && ` • ${new Date(item.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                          <Check className="size-3" />
                          Verified
                        </span>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <AnimatePresence initial={false}>
                        {review && !isEditMode && (
                          <motion.div
                            key="edit-btns"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className="flex items-center gap-1.5"
                          >
                            <button
                              onClick={() => handleExpand(item, 'edit')}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                              <Pencil className="size-3.5" />
                              Edit
                            </button>
                            <button
                              onClick={() => reviewId && handleDelete(reviewId)}
                              disabled={isDeleting}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                            >
                              {isDeleting ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-4" />}
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <AnimatePresence initial={false}>
                        {!review && !isWriteMode && (
                          <motion.div
                            key="review-btn"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                          >
                            <button
                              onClick={() => handleExpand(item, 'write')}
                              className="inline-flex items-center gap-1.5 rounded-full bg-hsecondary px-3 py-2 text-xs font-medium text-white hover:bg-hsecondary/90 transition-colors whitespace-nowrap"
                            >
                              <Star className="size-3.5" />
                              <span className="hidden sm:inline">Review This Product</span>
                              <span className="sm:hidden">Review</span>
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>
            );

            // ── Reviewed card ──
            if (review) {
              return (
                <div
                  key={itemKey}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  {productHeader}
                  <div className="border-t border-gray-100" />
                  <AnimatePresence mode="wait" initial={false}>
                    {isEditMode ? (
                      <motion.div
                        key="edit"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="bg-gray-50 p-4 sm:p-5 rounded-xl m-3 sm:m-6">
                          <h3 className="font-semibold text-gray-900 mb-5">Edit your review</h3>
                          <ReviewForm
                            productId={item.productId}
                            productVariantId={item.variantId}
                            orderId={item.orderId}
                            reviewId={reviewId}
                            initialValues={{
                              rating: review.rating,
                              reviewType: (review as any).reviewType,
                              review: review.review,
                            }}
                            onSuccess={handleFormSuccess}
                            onCancel={collapseForm}
                          />
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="review"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="p-4 pt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <StarRating rating={review.rating} size="sm" />
                            <span className="text-gray-300 text-xs">•</span>
                            {review.createdAt && (
                              <span className="text-xs text-hsecondary/40">
                                {new Date(review.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                          {(review as any).title && (
                            <p className="text-sm font-semibold text-gray-900 mb-1">{(review as any).title}</p>
                          )}
                          <p className="text-sm text-gray-600 leading-relaxed">{review.review}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            // ── Unreviewed card ──
            return (
              <div
                key={itemKey}
                className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              >
                {productHeader}
                <div className="border-t border-gray-100" />
                <AnimatePresence mode="wait" initial={false}>
                  {isWriteMode ? (
                    <motion.div
                      key="form"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="bg-gray-50 p-4 sm:p-5 rounded-xl m-3 sm:m-6">
                        <h3 className="font-semibold text-gray-900 mb-5">Write your review</h3>
                        <ReviewForm
                          productId={item.productId}
                          productVariantId={item.variantId}
                          orderId={item.orderId}
                          onSuccess={handleFormSuccess}
                          onCancel={collapseForm}
                        />
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="p-4 pt-3 flex items-center gap-2">
                        <StarRating rating={0} size="sm" />
                        <span className="text-xs text-gray-400">Not yet reviewed</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );

          })}
        </div>
      )}
    </div>
  );
}
